import { memo, useCallback, useMemo } from 'react';

import { useToggleViewVisibilityMenuOption } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useToggleViewVisibilityMenuOption';
import { useToggleViewVisibilityMenuOptions } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useToggleViewVisibilityMenuOptions';
import { ViewVisibilityHeaderToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/shared/ViewVisibilityHeaderToggle';
import { ViewVisibilitySection } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilitySection/ViewVisibilitySection';
import { ViewVisibilityToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilitySection/ViewVisibilityToggle';
import { ConfigVisibilityToggleType } from '~/constants/configViews';
import { getConfigFieldLabel } from '~/constants/SortFieldsMap';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type ConfigViewOption } from '~/store/configViews/types';

export type ConfigViewVisbilityOptionsProps = {
  options: ConfigViewOption[];
};

export const ConfigViewVisbilityOptions = memo(({ options: rawOptions }: ConfigViewVisbilityOptionsProps) => {
  // Read here rather than at the call sites: private and public menus both render this and both keep the legacy copy
  // while the redesign is gated.
  const { ui2026 } = useTemporaryFeatureFlags();
  const { toggleViewVisibilityMenuOption } = useToggleViewVisibilityMenuOption();
  const { toggleViewVisibilityMenuOptions } = useToggleViewVisibilityMenuOptions();

  // The redesign renames the "Asset details" toggle. The copy lives in getConfigFieldLabel, but the selector that builds
  // the default label cannot read the flag, so relabel here for every row this menu renders (single header switch or the
  // eye toggle in the Metadata section).
  const options = useMemo(
    () =>
      ui2026
        ? rawOptions.map((option) =>
            option.fieldName === ConfigVisibilityToggleType.titleAndMetadata
              ? { ...option, label: getConfigFieldLabel(option.fieldName, { ui2026 }) }
              : option,
          )
        : rawOptions,
    [rawOptions, ui2026],
  );
  const initialShowOptions = options.some(({ isVisible }) => isVisible);

  // Only used by the single-option branch, but hooks must run before that early return.
  const handleToggleSingleOption = useCallback(() => {
    toggleViewVisibilityMenuOption(options[0]);
  }, [options, toggleViewVisibilityMenuOption]);

  const handleToggleShowingOptions = useCallback(
    (showing: boolean) => {
      toggleViewVisibilityMenuOptions(options, showing);
    },
    [options, toggleViewVisibilityMenuOptions],
  );

  if (options.length === 1) {
    const option = options[0];

    return (
      <ViewVisibilityHeaderToggle
        key={option.fieldName}
        id={option.fieldName}
        label={option.label}
        onToggle={handleToggleSingleOption}
        isToggleOn={option.isVisible}
      />
    );
  }

  return (
    <ViewVisibilitySection
      initialShowOptions={initialShowOptions}
      id="info"
      label="Metadata"
      onToggleShowingOptions={handleToggleShowingOptions}
      toggles={options.map((option) => (
        <ViewVisibilityToggle key={option.fieldName} onToggleOption={toggleViewVisibilityMenuOption} option={option} />
      ))}
    />
  );
});

ConfigViewVisbilityOptions.displayName = 'ConfigViewVisbilityOptions';
